import React from "react";
import { I18n } from "../../../../i18n/i18n";
import { PlayerControls } from "../../../../model/playerConfiguration/PlayerControls";
import { ScenarioElement } from "../../../../model/playerConfiguration/ScenarioElement";
import { Subtitle } from "../../../../model/playerConfiguration/Subtitle";
import { ScenarioPath } from "../../../../model/scenarioPath/ScenarioPath";
import { ScenarioElementPlaybackState } from "../../../../uiModel/playback/ScenarioElementPlaybackState";
import { SocialShareConfiguration } from "../../../../uiModel/socialShare/SocialShareConfiguration";
import { TooltipMetadata } from "../../../../uiModel/tooltip/TooltipMetadata";
import { SocialShareIconComponent } from "../../../icons/socialShare/SocialShareIconComponent";
import { SubtitlesIconComponent } from "../../../icons/subtitles/SubtitlesIconComponent";
import { SocialShareComponent } from "../common/SocialShareComponent";
import { SubtitleSelectorComponent } from "../common/SubtitleSelectorComponent";
import { DesktopFullscreenControlComponent } from "./DesktopFullscreenControl";
import { DesktopSeekbarComponent } from "./DesktopSeekbarComponent";
import { ExpandableMenuButtonComponent } from "./ExpandableMenuButtonComponent";
import { MenuButtonWithTooltipComponent } from "./MenuButtonWithTooltipComponent";
import { PlaybackControlComponent } from "./PlaybackControlComponent";
import { ScenarioTimeComponent } from "./ScenarioTimeComponent";
import { VolumeControlComponent } from "./VolumeControlComponent";

import "@styles/c-desktopMenu.scss";

interface Props {
  i18n: I18n;
  configuration: PlayerControls;
  isDisabled: boolean;
  isDisplayed: boolean;
  socialShareConfiguration: SocialShareConfiguration;

  onMouseEnter: () => void;
  onMouseLeave: () => void;

  playbackState: ScenarioElementPlaybackState;
  onPlay: () => void;
  onPause: () => void;
  onReplay: () => void;
  onSeekStart: () => void;
  onSeekEnd: () => void;

  scenarioPath: ScenarioPath;
  totalTime: number;
  ellapsedTime: number;
  bufferedTime: number;
  onSeekTo: (time: number) => void;
  onJumpToScenarioElement: (scenarioElement: ScenarioElement, progress: number) => void;

  isVolumeMuted: boolean;
  volume: number;
  onVolumeChange: (volume: number) => void;
  onToggleMuted: () => void;

  subtitles: Subtitle[];
  selectedSubtitlesSrc?: string;
  onSubtitleSourceSelected: (subtitle?: Subtitle) => void;

  isFullscreenSupported: boolean;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;

  onCancelTooltip: () => void;
  onRequestTooltip: (event: TooltipMetadata) => void;

}

export const DesktopMenuComponent: React.SFC<Props> = (props: Props) => {
  const {
    i18n,
    configuration,
    isDisabled,
    isDisplayed,
    socialShareConfiguration,

    onMouseEnter,
    onMouseLeave,

    playbackState,
    onPlay,
    onPause,
    onReplay,
    onSeekStart,
    onSeekEnd,

    scenarioPath,
    totalTime,
    ellapsedTime,
    bufferedTime,
    onSeekTo,
    onJumpToScenarioElement,

    isVolumeMuted,
    volume,
    onVolumeChange,
    onToggleMuted,

    subtitles,
    onSubtitleSourceSelected,
    selectedSubtitlesSrc,

    isFullscreenSupported,
    isFullscreen,
    onToggleFullscreen,

    onCancelTooltip,
    onRequestTooltip
  } = props;

  return (
    <div
      className="c-desktopMenu"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={(evt) => evt.stopPropagation()}
    >
      <div className="c-desktopMenu__background">
        <div className="c-desktopMenu__backgroundAboveSeekbarAndMenu" />
        <div className="c-desktopMenu__seekbarAndMenuBackground" />
      </div>
      <div className="c-desktopMenu__seekbarAndMenu">
        {configuration.seekbarEnabled && scenarioPath &&
          <div className="c-desktopMenu__seekbar">
            <DesktopSeekbarComponent
              i18n={i18n}
              scenarioPath={scenarioPath}
              totalTime={totalTime}
              ellapsedTime={ellapsedTime}
              bufferedTime={bufferedTime}
              onSeekTo={onSeekTo}
              onJumpToScenarioElement={onJumpToScenarioElement}
              onHideTooltip={onCancelTooltip}
              onShowTooltip={onRequestTooltip}
              onSeekEnd={onSeekEnd}
              onSeekStart={onSeekStart}
            />
          </div>
        }
        <div className="c-desktopMenu__menu">
          {configuration.playPauseEnabled &&
            <PlaybackControlComponent
              className="c-desktopMenu__menuItem"
              playbackState={playbackState}
              onPlay={onPlay}
              onPause={onPause}
              onReplay={onReplay}
              onCancelTooltip={onCancelTooltip}
              onRequestTooltip={onRequestTooltip}
            />
          }
          <VolumeControlComponent
            isVolumeMuted={isVolumeMuted}
            volume={volume}
            onVolumeChange={onVolumeChange}
            onToggleMuted={onToggleMuted}
            onCancelTooltip={onCancelTooltip}
            onRequestTooltip={onRequestTooltip}
          />
          {configuration.seekbarEnabled &&
            <ScenarioTimeComponent
              ellapsedTime={ellapsedTime}
              totalTime={totalTime}
              isMultiElementScenario={scenarioPath.hasMultipleParts}
            />
          }
          <div className="c-desktopMenu__menuSpacer" />
          {configuration.socialButtonEnabled && socialShareConfiguration.canShare &&
            <ExpandableMenuButtonComponent
              className="c-desktopMenu__menuItem"
              canExpand={isDisplayed}
              icon={<SocialShareIconComponent />}
              menu={
                <SocialShareComponent
                  i18n={i18n}
                  facebookShareUrl={socialShareConfiguration.facebookUrl}
                  twitterShareUrl={socialShareConfiguration.twitterUrl}
                />
              }
              tooltipTarget="shareControl"
              onCancelTooltip={onCancelTooltip}
              onRequestTooltip={onRequestTooltip}
            />
          }
          {subtitles && !!subtitles.length &&
            (subtitles.length === 1 ?
              <MenuButtonWithTooltipComponent
                className="c-desktopMenu__menuItem"
                isActive={!!selectedSubtitlesSrc}
                icon={<SubtitlesIconComponent />}
                onClick={() => selectedSubtitlesSrc ? onSubtitleSourceSelected(undefined) : onSubtitleSourceSelected(subtitles[0])}
                tooltipTarget="subtitlesControl"
                onCancelTooltip={onCancelTooltip}
                onRequestTooltip={onRequestTooltip}
              /> :
              <ExpandableMenuButtonComponent
                className="c-desktopMenu__menuItem"
                canExpand={isDisplayed}
                isActive={!!selectedSubtitlesSrc}
                icon={<SubtitlesIconComponent />}
                menu={
                  <SubtitleSelectorComponent
                    i18n={i18n}
                    subtitles={subtitles}
                    onSubtitleSourceSelected={onSubtitleSourceSelected}
                    selectedSubtitlesSrc={selectedSubtitlesSrc}
                  />
                }
                tooltipTarget="subtitlesControl"
                onCancelTooltip={onCancelTooltip}
                onRequestTooltip={onRequestTooltip}
              />)

          }
          {isFullscreenSupported && configuration.fullscreenEnabled &&
            <DesktopFullscreenControlComponent
              isFullscreen={isFullscreen}
              onToggleFullscreen={onToggleFullscreen}
              onCancelTooltip={onCancelTooltip}
              onRequestTooltip={onRequestTooltip}
            />
          }
        </div>
        {isDisabled && <div className="c-desktopMenu__disabledCover" />}
      </div>

    </div>
  );
};
